import {
  Col,
  Flex,
  Form,
  Input,
  JsonEditor,
  type JsonValue,
  Paragraph,
  Row,
  Select,
  Spin,
  Switch,
  Textarea,
  type FormInstance,
  type SelectOption,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
} from "react";
import type { PurchaserConfigTemplate } from "@src/domain/entities/purchaser";
import {
  DISCOUNT_PERCENT_MAX,
  getSearchKeywordsError,
  PURCHASER_NAME_MAX_LENGTH,
  SEARCH_KEYWORDS_MAX_LENGTH,
  TERM_DAYS_MAX,
  validateSearchKeywords,
} from "./purchaserKeyValidation";
import {
  templateToFormDefaults,
  type PurchaserAddEditFormValues,
} from "./masterDataPurchaserFormUtils";

export type { PurchaserAddEditFormValues } from "./masterDataPurchaserFormUtils";

export interface CatalogPurchaserSelectOption {
  purchaserId: number;
  purchaserKey: string;
  purchaserName: string;
  configTemplate: PurchaserConfigTemplate | null;
}

export interface ProductCategorySelectOption {
  productCategoryId: number;
  name: string;
  categoryKey: string;
}

interface MasterDataPurchaserAddEditFormProps {
  form: FormInstance<PurchaserAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: PurchaserAddEditFormValues;
  catalogOptions: CatalogPurchaserSelectOption[];
  catalogLoading?: boolean;
  categoryOptions: ProductCategorySelectOption[];
  categoriesLoading?: boolean;
  onLoadConfigTemplate?: (
    purchaserId: number,
  ) => Promise<PurchaserConfigTemplate | null>;
  getCachedConfigTemplate?: (
    purchaserId: number,
  ) => PurchaserConfigTemplate | null | undefined;
  onSubmit: (values: PurchaserAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

function parseIdFromSelect(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "object" && "value" in (value as object)) {
    const raw = (value as SelectOption).value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseStoredJson(value: string | undefined): JsonValue {
  const trimmed = (value ?? "").trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed != null && typeof parsed === "object") {
      return parsed as JsonValue;
    }
    return {};
  } catch {
    return {};
  }
}

interface SearchKeywordsFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
}

const SearchKeywordsField = ({
  value,
  onChange,
  onBlur,
  isDisabled = false,
  isLoading = false,
  isError = false,
}: SearchKeywordsFieldProps) => {
  const formatError = getSearchKeywordsError(value);

  return (
    <>
      <Spin spinning={isLoading} size="small">
        <Textarea
          value={value ?? ""}
          onChange={(event) => onChange?.(event.target.value)}
          onBlur={onBlur}
          placeholder="Keyword | Keyword"
          maxLength={SEARCH_KEYWORDS_MAX_LENGTH}
          minRows={2}
          isDisabled={isDisabled}
          isError={Boolean(formatError) || isError}
        />
      </Spin>
      {formatError ? (
        <Paragraph className="search-keywords-format-error m-0">
          * {formatError}
        </Paragraph>
      ) : null}
    </>
  );
};

interface JsonEditorFieldProps {
  value?: string;
  onChange?: (next: string) => void;
  rootName: string;
  isDisabled?: boolean;
}

const JsonEditorField = ({
  value,
  onChange,
  rootName,
  isDisabled = false,
}: JsonEditorFieldProps) => {
  return (
    <JsonEditor
      value={parseStoredJson(value)}
      onChange={(next) => onChange?.(JSON.stringify(next, null, 2))}
      rootName={rootName}
      collapse={2}
      readOnly={isDisabled}
    />
  );
};

interface IdSelectFieldProps {
  value?: number | null;
  onChange?: (value: number | null) => void;
  options: SelectOption[];
  isLoading?: boolean;
  isClearable?: boolean;
  selectKey: string;
  placeholder: string;
  ariaLabel: string;
}

const IdSelectField = ({
  value,
  onChange,
  options,
  isLoading = false,
  isClearable = false,
  selectKey,
  placeholder,
  ariaLabel,
}: IdSelectFieldProps) => {
  const selected = options.find((option) => option.value === String(value));
  return (
    <Select
      key={`${selectKey}-${value ?? "none"}-${options.length}`}
      size="medium"
      isSearchable
      isClearable={isClearable}
      isLoading={isLoading}
      placeholder={placeholder}
      options={options}
      defaultValue={selected}
      onChange={(next) => onChange?.(parseIdFromSelect(next))}
      aria-label={ariaLabel}
    />
  );
};

const JSON_FIELD_RULE = {
  validator: async (_: unknown, value: string | undefined) => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) return;
    try {
      JSON.parse(trimmed);
    } catch {
      throw new TypeError("Must be valid JSON");
    }
  },
};

const PERCENT_FIELD_RULE = {
  validator: async (_: unknown, value: string | undefined) => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) return;
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
      throw new TypeError(
        `Must be a number between 0 and ${DISCOUNT_PERCENT_MAX} with at most 2 decimal places`,
      );
    }
    const parsed = Number(trimmed);
    if (parsed < 0 || parsed > DISCOUNT_PERCENT_MAX) {
      throw new TypeError(`Must be between 0 and ${DISCOUNT_PERCENT_MAX}`);
    }
  },
};

const INTEGER_FIELD_RULE = (max?: number) => ({
  validator: async (_: unknown, value: string | undefined) => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) return;
    if (!/^\d+$/.test(trimmed)) {
      throw new TypeError("Please enter a valid number.");
    }
    const parsed = Number(trimmed);
    if (max != null && parsed > max) {
      throw new TypeError(`Must be between 0 and ${max}`);
    }
  },
});

export const MasterDataPurchaserAddEditForm = ({
  form,
  mode,
  initialValues,
  catalogOptions,
  catalogLoading = false,
  categoryOptions,
  categoriesLoading = false,
  onLoadConfigTemplate,
  getCachedConfigTemplate,
  onSubmit,
  onFormValidityChange,
}: MasterDataPurchaserAddEditFormProps) => {
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const templateLoadIdRef = useRef(0);

  const catalogSelectOptions = useMemo<SelectOption[]>(
    () =>
      catalogOptions.map((purchaser) => ({
        value: String(purchaser.purchaserId),
        label: purchaser.purchaserName,
      })),
    [catalogOptions],
  );

  const categorySelectOptions = useMemo<SelectOption[]>(
    () =>
      categoryOptions.map((category) => ({
        value: String(category.productCategoryId),
        label: category.name,
      })),
    [categoryOptions],
  );

  const applyCatalogSelection = useCallback(
    async (catalogPurchaserId: number | null) => {
      const loadId = ++templateLoadIdRef.current;
      const selected = catalogOptions.find(
        (option) => option.purchaserId === catalogPurchaserId,
      );

      if (!selected) {
        setIsTemplateLoading(false);
        form.setFieldsValue({
          purchaserKey: "",
          ...templateToFormDefaults(null),
        });
        return;
      }

      const cached = getCachedConfigTemplate?.(selected.purchaserId);
      if (cached !== undefined) {
        setIsTemplateLoading(false);
        form.setFieldsValue({
          purchaserKey: selected.purchaserKey,
          ...templateToFormDefaults(cached),
        });
        return;
      }

      setIsTemplateLoading(true);
      onFormValidityChange?.(false);
      form.setFieldsValue({
        purchaserKey: selected.purchaserKey,
        ...templateToFormDefaults(null),
      });

      try {
        const template = onLoadConfigTemplate
          ? await onLoadConfigTemplate(selected.purchaserId)
          : null;
        if (loadId !== templateLoadIdRef.current) return;
        form.setFieldsValue({
          purchaserKey: selected.purchaserKey,
          ...templateToFormDefaults(template),
        });
      } finally {
        if (loadId === templateLoadIdRef.current) {
          setIsTemplateLoading(false);
        }
      }
    },
    [
      catalogOptions,
      form,
      getCachedConfigTemplate,
      onFormValidityChange,
      onLoadConfigTemplate,
    ],
  );

  const updateFormValidity = useCallback(() => {
    if (isTemplateLoading) {
      onFormValidityChange?.(false);
      return;
    }

    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const purchaserName = values.purchaserName?.trim() ?? "";
    const purchaserKey = values.purchaserKey?.trim() ?? "";

    const hasRequiredValues =
      mode === "create"
        ? values.catalogPurchaserId != null && purchaserKey !== ""
        : purchaserName !== "" && purchaserKey !== "";

    const isDirty =
      mode === "create"
        ? true
        : purchaserName !== (initialValues.purchaserName?.trim() ?? "") ||
          Boolean(values.isActive) !== Boolean(initialValues.isActive) ||
          (values.searchKeywords ?? "") !==
            (initialValues.searchKeywords ?? "") ||
          Number(values.defaultProductCategoryId) !==
            Number(initialValues.defaultProductCategoryId) ||
          (values.metadata ?? "") !== (initialValues.metadata ?? "") ||
          (values.intelligenceFilter ?? "") !==
            (initialValues.intelligenceFilter ?? "") ||
          (values.discountPercentageCredit ?? "") !==
            (initialValues.discountPercentageCredit ?? "") ||
          (values.deductionTypeInvoicePatternForDiscount ?? "") !==
            (initialValues.deductionTypeInvoicePatternForDiscount ?? "") ||
          (values.discountPercentageDeduction ?? "") !==
            (initialValues.discountPercentageDeduction ?? "") ||
          (values.discountTermAgreementDays ?? "") !==
            (initialValues.discountTermAgreementDays ?? "") ||
          (values.additionalFieldMapping ?? "") !==
            (initialValues.additionalFieldMapping ?? "") ||
          (values.discountTermAgreementDaysTolerance ?? "") !==
            (initialValues.discountTermAgreementDaysTolerance ?? "");

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, isTemplateLoading, mode, onFormValidityChange]);

  useEffect(() => {
    templateLoadIdRef.current += 1;
    setIsTemplateLoading(false);
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  useEffect(() => {
    updateFormValidity();
  }, [updateFormValidity]);

  const nameRules = useMemo(
    () => [
      { required: true, message: "Purchaser name is required" },
      {
        max: PURCHASER_NAME_MAX_LENGTH,
        message: `Purchaser name must be less than or equal to ${PURCHASER_NAME_MAX_LENGTH} characters.`,
      },
    ],
    [],
  );

  const keywordRules = useMemo(
    () => [
      {
        max: SEARCH_KEYWORDS_MAX_LENGTH,
        message: `Search keywords must be less than or equal to ${SEARCH_KEYWORDS_MAX_LENGTH} characters.`,
      },
      {
        validator: async (_: unknown, value: unknown) => {
          await validateSearchKeywords(value);
        },
      },
    ],
    [],
  );

  const jsonRules = useMemo(() => [JSON_FIELD_RULE], []);
  const percentRules = useMemo(() => [PERCENT_FIELD_RULE], []);
  const termDaysRules = useMemo(() => [INTEGER_FIELD_RULE(TERM_DAYS_MAX)], []);
  const toleranceRules = useMemo(() => [INTEGER_FIELD_RULE()], []);

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        onValuesChange={(changed) => {
          if (
            mode === "create" &&
            Object.hasOwn(changed, "catalogPurchaserId")
          ) {
            void applyCatalogSelection(changed.catalogPurchaserId ?? null).then(
              () => updateFormValidity(),
            );
          }
          updateFormValidity();
        }}
        className="master-data-purchaser-add-edit"
      >
        <Row>
          <Col span={6}>
            {mode === "create" ? (
              <Form.Item
                name="catalogPurchaserId"
                label="Purchaser Name"
                validateTrigger={["onChange", "onBlur"]}
                rules={[
                  {
                    validator: async (_: unknown, value: unknown) => {
                      if (value == null || Number(value) <= 0) {
                        throw new Error("Purchaser name is required");
                      }
                    },
                  },
                ]}
              >
                <IdSelectField
                  options={catalogSelectOptions}
                  isLoading={catalogLoading}
                  selectKey={`catalog-purchaser-${initialValues.catalogPurchaserId ?? "new"}`}
                  placeholder="Select purchaser"
                  ariaLabel="Purchaser name"
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="purchaserName"
                label="Purchaser Name"
                validateTrigger={["onChange", "onBlur"]}
                rules={nameRules}
              >
                <Input placeholder="Purchaser name" />
              </Form.Item>
            )}
          </Col>
          <Col span={6}>
            <Form.Item name="purchaserKey" label="Purchaser Key">
              <Input placeholder="Purchaser key" isDisabled />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={2}>
            <Flex
              align="start"
              gap={1}
              className="status-switch"
              direction="column"
            >
              <Paragraph className="m-0 custom-label">Status</Paragraph>
              <Form.Item name="isActive" valuePropName="isSelected" noStyle>
                <Switch
                  className="p-0 mt-1"
                  size="small"
                  aria-label="Purchaser active status"
                />
              </Form.Item>
            </Flex>
          </Col>
          <Col span={4}>
            <Form.Item
              name="searchKeywords"
              label="Search Keywords"
              className="search-keywords-item"
              validateTrigger={["onChange", "onBlur"]}
              rules={keywordRules}
            >
              <SearchKeywordsField
                isLoading={isTemplateLoading}
                isDisabled={isTemplateLoading}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="defaultProductCategoryId"
              label="Default Product Category"
            >
              <IdSelectField
                options={categorySelectOptions}
                isLoading={categoriesLoading}
                isClearable
                selectKey={`category-select-${mode}-${initialValues.defaultProductCategoryId ?? "none"}`}
                placeholder="Select category"
                ariaLabel="Default product category"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Spin spinning={isTemplateLoading} size="small">
              <Form.Item
                name="metadata"
                label="Metadata (JSON)"
                validateTrigger={["onChange", "onBlur"]}
                rules={jsonRules}
              >
                <JsonEditorField
                  rootName="metadata"
                  isDisabled={isTemplateLoading}
                />
              </Form.Item>
            </Spin>
          </Col>
          <Col span={6}>
            <Spin spinning={isTemplateLoading} size="small">
              <Form.Item
                name="intelligenceFilter"
                label="Intelligence Filter (JSON)"
                validateTrigger={["onChange", "onBlur"]}
                rules={jsonRules}
              >
                <JsonEditorField
                  rootName="intelligenceFilter"
                  isDisabled={isTemplateLoading}
                />
              </Form.Item>
            </Spin>
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Spin spinning={isTemplateLoading} size="small">
              <Form.Item
                name="deductionTypeInvoicePatternForDiscount"
                label="Deduction Invoice Pattern (JSON)"
                validateTrigger={["onChange", "onBlur"]}
                rules={jsonRules}
              >
                <JsonEditorField
                  rootName="deductionTypeInvoicePatternForDiscount"
                  isDisabled={isTemplateLoading}
                />
              </Form.Item>
            </Spin>
          </Col>
          <Col span={6}>
            <Spin spinning={isTemplateLoading} size="small">
              <Form.Item
                name="additionalFieldMapping"
                label="Additional Field Mapping (JSON)"
                validateTrigger={["onChange", "onBlur"]}
                rules={jsonRules}
              >
                <JsonEditorField
                  rootName="additionalFieldMapping"
                  isDisabled={isTemplateLoading}
                />
              </Form.Item>
            </Spin>
          </Col>
        </Row>

        <Row>
          <Col span={3}>
            <Form.Item
              name="discountPercentageCredit"
              label="Credit Term Discount %"
              validateTrigger={["onChange", "onBlur"]}
              rules={percentRules}
            >
              <Input placeholder="e.g. 1.50" />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item
              name="discountPercentageDeduction"
              label="Deduction Term Discount %"
              validateTrigger={["onChange", "onBlur"]}
              rules={percentRules}
            >
              <Input placeholder="e.g. 1.00" />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item
              name="discountTermAgreementDays"
              label="Term Agreement Days"
              validateTrigger={["onChange", "onBlur"]}
              rules={termDaysRules}
            >
              <Input placeholder="e.g. 60" />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Spin spinning={isTemplateLoading} size="small">
              <Form.Item
                name="discountTermAgreementDaysTolerance"
                label="Term Days Tolerance"
                validateTrigger={["onChange", "onBlur"]}
                rules={toleranceRules}
              >
                <Input placeholder="e.g. 2" isDisabled={isTemplateLoading} />
              </Form.Item>
            </Spin>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
